import { useEffect, useState } from "react";
import { X as XIcon, MessageCircle, ExternalLink, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { useLocalePath } from "./LangRouting";

const LS_KEY = "puregloss_whatsapp_consent";

const hasConsent = () => {
  try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
};
const grantConsent = () => {
  try { localStorage.setItem(LS_KEY, "1"); } catch { /* ignore */ }
};

/**
 * Global click-interceptor: on the first click of any anchor pointing to
 * wa.me / whatsapp.com/send, show a short GDPR notice explaining that the
 * user is about to be transferred to WhatsApp (Meta) and that the
 * transmission includes phone number + message. On confirm we persist the
 * consent in localStorage and open WhatsApp — future clicks pass through.
 */
export const WhatsAppConsentGate = () => {
  const { t } = useLang();
  const lp = useLocalePath();
  const [pendingHref, setPendingHref] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      // Find the closest anchor (link may be wrapped in <button>).
      const a = e.target && e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const isWa = /^https?:\/\/(wa\.me|api\.whatsapp\.com|(www\.)?whatsapp\.com\/send)/i.test(href);
      if (!isWa) return;
      if (hasConsent()) return;               // already accepted → let through
      e.preventDefault();
      e.stopPropagation();
      setPendingHref(href);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  if (!pendingHref) return null;

  const confirm = () => {
    grantConsent();
    const url = pendingHref;
    setPendingHref(null);
    try { window.open(url, "_blank", "noopener,noreferrer"); } catch { /* ignore */ }
  };
  const cancel = () => setPendingHref(null);

  return (
    <div
      data-testid="whatsapp-consent-modal"
      className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-md p-4"
      onClick={cancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#0a0a0c] border border-gold/40 shadow-[0_0_100px_-20px_rgba(212,175,55,0.5)] p-6 md:p-7"
      >
        <button
          type="button"
          onClick={cancel}
          aria-label={t.whatsappConsent.close}
          data-testid="whatsapp-consent-close"
          className="absolute top-3 right-3 text-zinc-400 hover:text-gold p-2"
        >
          <XIcon size={16} />
        </button>

        <div className="flex items-center gap-3">
          <span className="w-11 h-11 border border-gold/50 bg-gold/10 flex items-center justify-center">
            <ShieldAlert size={18} className="text-gold" />
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-gold">
            {t.whatsappConsent.eyebrow}
          </span>
        </div>

        <h2 className="mt-4 font-heading text-white text-xl md:text-2xl uppercase tracking-tight leading-tight">
          {t.whatsappConsent.title}
        </h2>
        <span className="block w-10 h-px bg-gold mt-4" />

        <p className="mt-4 text-zinc-300 text-sm leading-relaxed">
          {t.whatsappConsent.body}
        </p>
        <p className="mt-3 text-zinc-500 text-xs leading-relaxed">
          {t.whatsappConsent.details}{" "}
          <Link to={lp("/datenschutz")} className="text-gold underline underline-offset-2 hover:text-[#f3e5ab]">
            {t.whatsappConsent.datenschutzLink}
          </Link>
          .
        </p>

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={cancel}
            data-testid="whatsapp-consent-cancel"
            className="flex-1 border border-white/20 text-white px-4 py-3 text-xs tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors"
          >
            {t.whatsappConsent.cancel}
          </button>
          <button
            type="button"
            onClick={confirm}
            data-testid="whatsapp-consent-confirm"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-black px-4 py-3 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#f3e5ab] transition-colors"
          >
            <MessageCircle size={14} /> {t.whatsappConsent.confirm}
            <ExternalLink size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};
